import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import {
  TrendingUp, 
  Wallet, 
  RefreshCw,
  DollarSign,
  Calendar,
  Clock,
  Zap,
  AlertCircle
} from 'lucide-react';

const AUSTRALIA_TIMEZONE = 'Asia/Dubai';
const INDIA_TIMEZONE = 'Asia/Kolkata';

const ROIWallet = () => {
  const CACHE_TTL_MS = 30000;
  const [walletBalance, setWalletBalance] = useState(0);
  const [roiSchedules, setRoiSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDailyROI: 0,
    pendingToday: 0,
    activeSchedules: 0,
    pausedSchedules: 0,
    blockedSchedules: 0
  });

  const userId = localStorage.getItem('userId');
  const userDbKey = localStorage.getItem('dbKey');
  const userRegistrationDate = localStorage.getItem('registrationDate');

  useEffect(() => {
    if (userId && userDbKey) {
      fetchROIWallet();
    }
  }, [userId, userDbKey]);

  const getDateKeyInTimezone = (date, tz = AUSTRALIA_TIMEZONE) => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };

  const parseDateKeyToAESTRunDate = (dateKey) => {
    const [year, month, day] = (dateKey || '').split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - (4 * 60 * 60 * 1000));
  };

  const fetchROIWallet = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const cacheKey = `roi_wallet_cache_${userId || 'guest'}`;
      if (!forceRefresh) {
        const raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (Date.now() - cached.timestamp < CACHE_TTL_MS) {
            setWalletBalance(cached.walletBalance || 0);
            setRoiSchedules(cached.roiSchedules || []);
            setStats(cached.stats || {
              totalDailyROI: 0,
              pendingToday: 0,
              activeSchedules: 0,
              pausedSchedules: 0,
              blockedSchedules: 0
            });
            setLoading(false);
            return;
          }
        }
      }
      
      // Fetch user wallet balance
      let roiWalletBalance = 0;
      if (userRegistrationDate && userDbKey) {
        const userRef = ref(database, `users/${userRegistrationDate}/${userDbKey}`);
        const userSnapshot = await get(userRef);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          // Check multiple possible wallet field names
          roiWalletBalance = 
            parseFloat(userData.wallets?.roi) || 
            parseFloat(userData.wallets?.roiWallet) || 
            parseFloat(userData.roiWallet) || 
            0;
          setWalletBalance(roiWalletBalance);
        }
      }
      
      // Fetch ROI schedules
      const roiRef = ref(database, 'ROISchedule');
      const roiSnapshot = await get(roiRef);
      
      const schedules = [];
      let totalDailyROI = 0;
      let pendingToday = 0;
      let activeSchedules = 0;
      let pausedSchedules = 0;
      let blockedSchedules = 0;
      
      // Get current date in fixed GST timezone
      const now = new Date();
      const serverNow = new Date(now.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
      const todayStr = getDateKeyInTimezone(now, AUSTRALIA_TIMEZONE);
      
      console.log('Server time (GST):', serverNow.toString());
      console.log('Today date:', todayStr);
      
      if (roiSnapshot.exists()) {
        const data = roiSnapshot.val();
        
        // Search through all date folders
        Object.entries(data).forEach(([date, users]) => {
          Object.entries(users).forEach(([userKey, userSchedules]) => {
            // Check if this is our user's schedules
            const isUserSchedule = 
              userKey === userId || 
              userKey === userDbKey ||
              (userSchedules && Object.values(userSchedules).some(s => s.userId === userId || s.sponsorId === userId));
            
            if (isUserSchedule) {
              Object.entries(userSchedules).forEach(([scheduleId, schedule]) => {
                const dailyROIAmount = parseFloat(schedule.dailyROIAmount) || 
                  ((parseFloat(schedule.investmentAmount) || 0) * (parseFloat(schedule.dailyROI) || 0)) / 100;
                
                const roiStatus = schedule.roiStatus || schedule.status || 'active';
                const isPaused = roiStatus === 'paused';
                const isBlocked = roiStatus === 'blocked';
                const isActive = roiStatus === 'active' && !isBlocked;
                
                // Parse next ROI date in server timezone
                let nextRoiDate = null;
                let nextRoiDateObj = null;
                let isToday = false;
                
                if (schedule.nextRoiDateKey || schedule.nextRoiDate) {
                  try {
                    // Prefer backend GST date key for strict run matching
                    if (schedule.nextRoiDateKey) {
                      nextRoiDateObj = parseDateKeyToAESTRunDate(schedule.nextRoiDateKey);
                    } else {
                      nextRoiDateObj = new Date(schedule.nextRoiDate);
                    }
                    
                    // Format for display in Dubai time
                    const nextRoiDateAusDate = nextRoiDateObj.toLocaleDateString('en-US', {
                      timeZone: AUSTRALIA_TIMEZONE,
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                    const nextRoiDateAusTime = nextRoiDateObj.toLocaleTimeString('en-US', {
                      timeZone: AUSTRALIA_TIMEZONE,
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });
                    nextRoiDate = `${nextRoiDateAusDate} ${nextRoiDateAusTime} (Dubai)`;
                    
                    // Check if next ROI is today (in GST)
                    const nextRoiDateStr = schedule.nextRoiDateKey || getDateKeyInTimezone(nextRoiDateObj, AUSTRALIA_TIMEZONE);
                    isToday = nextRoiDateStr === todayStr;
                    
                    // Check if next ROI is within the next hour
                    const timeDiff = nextRoiDateObj - serverNow;
                    const isWithinHour = timeDiff > 0 && timeDiff < 60 * 60 * 1000;
                    
                    console.log(`Schedule ${scheduleId}: Next ROI: ${nextRoiDate}, Today: ${isToday}, Within hour: ${isWithinHour}`);
                    
                  } catch (e) {
                    console.error('Error parsing nextRoiDate:', e);
                    nextRoiDate = schedule.nextRoiDate || schedule.nextRoiDateKey;
                  }
                }
                
                const scheduleItem = {
                  id: scheduleId,
                  date: date,
                  userId: schedule.userId || userId,
                  userName: schedule.userName || 'User',
                  packageName: schedule.packageName || 'Package',
                  investmentAmount: parseFloat(schedule.investmentAmount) || 0,
                  dailyROI: parseFloat(schedule.dailyROI) || 0,
                  dailyROIAmount: dailyROIAmount,
                  roiStatus: roiStatus,
                  startDate: schedule.startDate,
                  nextRoiDate: nextRoiDate,
                  nextRoiDateRaw: schedule.nextRoiDate,
                  nextRoiDateKey: schedule.nextRoiDateKey || null,
                  isToday: isToday,
                  isPaused: isPaused,
                  isBlocked: isBlocked,
                  isActive: isActive,
                  workingDays: schedule.workingDays || 'Mon-Fri'
                };
                
                schedules.push(scheduleItem);
                
                if (isActive) {
                  totalDailyROI += dailyROIAmount;
                  activeSchedules++;
                  
                  // Check if pending for today
                  if (isToday) {
                    pendingToday += dailyROIAmount;
                  }
                } else if (isPaused) {
                  pausedSchedules++;
                } else if (isBlocked) {
                  blockedSchedules++;
                }
              });
            }
          });
        });
      }
      
      // Fetch actual credited ROI amounts from roiIncomes
      try {
        const roiIncomesRef = ref(database, 'roiIncomes');
        const roiIncomesSnapshot = await get(roiIncomesRef);
        
        if (roiIncomesSnapshot.exists()) {
          const incomesData = roiIncomesSnapshot.val();
          let totalCreditedROI = 0;
          
          // Search through all dates
          Object.entries(incomesData).forEach(([date, users]) => {
            // Check if this date has our user
            Object.entries(users).forEach(([uid, incomes]) => {
              if (uid === userDbKey || uid === userId) {
                Object.entries(incomes).forEach(([incomeId, income]) => {
                  // Only count ROI incomes (not energy wallet)
                  if (income.type !== 'energy_wallet' && income.status !== 'blocked') {
                    totalCreditedROI += parseFloat(income.amount) || 0;
                  }
                });
              }
            });
          });
          
          // Update wallet balance if we have credited amounts
          if (totalCreditedROI > 0 && walletBalance === 0) {
            setWalletBalance(totalCreditedROI);
          }
        }
      } catch (error) {
        console.error('Error fetching ROI incomes:', error);
      }
      
      // Sort by next ROI date
      schedules.sort((a, b) => {
      const dateA = a.nextRoiDateRaw ? new Date(a.nextRoiDateRaw) : new Date(8640000000000000);
      const dateB = b.nextRoiDateRaw ? new Date(b.nextRoiDateRaw) : new Date(8640000000000000);
        if (a.nextRoiDateKey && b.nextRoiDateKey) {
          return a.nextRoiDateKey.localeCompare(b.nextRoiDateKey);
        }
        return dateA - dateB;
      });
      
      setRoiSchedules(schedules);
      setStats({
        totalDailyROI,
        pendingToday,
        activeSchedules,
        pausedSchedules,
        blockedSchedules
      });

      sessionStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        walletBalance: roiWalletBalance,
        roiSchedules: schedules,
        stats: {
          totalDailyROI,
          pendingToday,
          activeSchedules,
          pausedSchedules,
          blockedSchedules
        }
      }));
    } catch (error) {
      console.error('Error fetching ROI wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dateString; // Already formatted with timezone
  };

  const getStatusBadge = (schedule) => {
    if (schedule.isBlocked) {
      return <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 border border-red-500/30">
        Blocked
      </span>;
    }
    if (schedule.isPaused) {
      return <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
        Paused
      </span>;
    }
    return <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/30">
      Active
    </span>;
  };

  const getNextRoiStatus = (schedule) => {
    if (!schedule.nextRoiDateRaw) return null;
    
    try {
      const now = new Date();
      const serverNow = new Date(now.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
      const nextDate = schedule.nextRoiDateKey
        ? parseDateKeyToAESTRunDate(schedule.nextRoiDateKey)
        : new Date(schedule.nextRoiDateRaw);
      
      const timeDiff = nextDate - serverNow;
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      if (timeDiff < 0) {
        return { text: 'Processing', color: 'text-yellow-400' };
      } else if (hoursDiff < 1) {
        return { text: 'Within 1 hour', color: 'text-yellow-400' };
      } else if (hoursDiff < 24) {
        return { text: 'Today', color: 'text-green-400' };
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">AV ROI Wallet</h1>
        <p className="text-gray-400 text-sm mt-1">Your ROI earnings and schedules (Dubai Time)</p>
      </div>

      {/* Wallet Balance Card */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 mb-6 border border-green-500/30 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-5 w-5 text-green-400" />
              <h3 className="text-gray-300 text-sm">ROI Wallet Balance</h3>
            </div>
            <div className="text-3xl font-bold text-green-400">{formatCurrency(walletBalance)}</div>
            <p className="text-gray-500 text-xs mt-2">Accumulated ROI earnings</p>
          </div>
          <div className="p-4 bg-green-500/10 rounded-full">
            <TrendingUp className="h-8 w-8 text-green-400" />
          </div>
        </div>
      </div>

      {/* Stats Grid - Mobile Friendly */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Daily ROI</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalDailyROI)}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Pending Today</div>
          <div className="text-lg font-bold text-blue-400">{formatCurrency(stats.pendingToday)}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Active</div>
          <div className="text-lg font-bold text-green-400">{stats.activeSchedules}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Blocked</div>
          <div className="text-lg font-bold text-red-400">{stats.blockedSchedules}</div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="mb-6">
        <button
          onClick={() => fetchROIWallet(true)}
          className="w-full sm:w-auto px-4 py-3 bg-primary hover:bg-primary/80 rounded-xl text-white flex items-center justify-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh Data
        </button>
      </div>

      {/* ROI Schedules */}
      <div>
        <h3 className="text-lg font-bold text-white mb-4">Your ROI Schedules</h3>
        
        {roiSchedules.length > 0 ? (
          <div className="space-y-4">
            {roiSchedules.map((schedule) => {
              const nextRoiStatus = getNextRoiStatus(schedule);
              
              return (
                <div key={schedule.id} className="bg-gray-800/30 rounded-xl p-5 border border-gray-700">
                  {/* Header with Package Name and Status */}
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-white font-bold text-lg">{schedule.packageName}</h4>
                    {getStatusBadge(schedule)}
                  </div>
                  
                  {/* Mobile-friendly grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-900/50 p-3 rounded-lg">
                      <div className="text-gray-400 text-xs mb-1">Investment</div>
                      <div className="text-white font-bold">{formatCurrency(schedule.investmentAmount)}</div>
                    </div>
                    <div className="bg-gray-900/50 p-3 rounded-lg">
                      <div className="text-gray-400 text-xs mb-1">Daily ROI</div>
                      <div className="text-green-400 font-bold">{formatCurrency(schedule.dailyROIAmount)}</div>
                      <div className="text-gray-500 text-xs">({schedule.dailyROI}%)</div>
                    </div>
                  </div>
                  
                  {/* Next ROI Date - Highlighted */}
                  <div className="bg-gray-900/50 p-3 rounded-lg mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        <span className="text-gray-300 text-sm">Next ROI:</span>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-medium">{formatDate(schedule.nextRoiDate)}</div>
                        {nextRoiStatus && (
                          <span className={`text-xs ${nextRoiStatus.color}`}>
                            {nextRoiStatus.text}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Status Message */}
                  {schedule.isActive && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 text-sm flex items-center gap-2">
                        <span className="text-lg">✅</span>
                        ROI is actively being credited to your wallet daily.
                        {schedule.isToday && (
                          <span className="text-yellow-400 ml-2">(Today's ROI pending)</span>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {schedule.isPaused && (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-yellow-400 text-sm flex items-center gap-2">
                        <span className="text-lg">⏸️</span>
                        This ROI schedule is paused. ROI earnings are temporarily stopped.
                      </p>
                    </div>
                  )}
                  
                  {schedule.isBlocked && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm flex items-center gap-2">
                        <span className="text-lg">🚫</span>
                        This ROI schedule is blocked. You are not receiving ROI from this package.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-white mb-2">No ROI Schedules Found</h3>
            <p className="text-gray-400 mb-6">You don't have any active ROI schedules yet</p>
            <button
              onClick={() => window.location.href = '/user-dashboard/activate'}
              className="px-6 py-3 bg-primary hover:bg-primary/80 rounded-xl text-white font-bold"
            >
              Buy Your First Package
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROIWallet;




